import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { listGLAccounts, createGLAccount, updateGLAccount, deleteGLAccount, seedGLAccounts } from '../api';

const CATEGORY_OPTIONS = [
  { value: 'ASSET', label: 'Asset' },
  { value: 'LIABILITY', label: 'Liability' },
  { value: 'EQUITY', label: 'Equity' },
  { value: 'REVENUE', label: 'Revenue' },
  { value: 'EXPENSE', label: 'Expense' },
];

const SUB_CATEGORY_MAP = {
  ASSET: [
    { value: 'CURRENT_ASSET', label: 'Current Asset' },
    { value: 'FIXED_ASSET', label: 'Fixed Asset' },
    { value: 'OTHER_ASSET', label: 'Other Asset' },
  ],
  LIABILITY: [
    { value: 'CURRENT_LIABILITY', label: 'Current Liability' },
    { value: 'LONG_TERM_LIABILITY', label: 'Long-term Liability' },
  ],
  EQUITY: [
    { value: 'OWNERS_EQUITY', label: "Owner's Equity" },
    { value: 'RETAINED_EARNINGS', label: 'Retained Earnings' },
  ],
  REVENUE: [
    { value: 'OPERATING_REVENUE', label: 'Operating Revenue' },
    { value: 'OTHER_REVENUE', label: 'Other Revenue' },
  ],
  EXPENSE: [
    { value: 'OPERATING_EXPENSE', label: 'Operating Expense' },
    { value: 'COST_OF_SERVICES', label: 'Cost of Services' },
    { value: 'ADMINISTRATIVE', label: 'Administrative' },
    { value: 'OTHER_EXPENSE', label: 'Other Expense' },
  ],
};

function ChartOfAccounts({ authAxios, showFormMessage, userRole }) {
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [formData, setFormData] = useState({ code: '', name: '', category: 'ASSET', sub_category: 'CURRENT_ASSET', description: '', is_active: true });

  const canManage = useMemo(() => ['PARTNER', 'SUPERUSER', 'ACCOUNTANT'].includes(userRole), [userRole]);

  const fetchAccounts = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await listGLAccounts(authAxios);
      setAccounts(Array.isArray(data) ? data : []);
    } catch (err) {
      showFormMessage('Failed to load chart of accounts.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [authAxios, showFormMessage]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const handleSeed = async () => {
    try {
      await seedGLAccounts(authAxios);
      showFormMessage('Default accounts seeded successfully.', 'success');
      fetchAccounts();
    } catch (err) {
      showFormMessage('Failed to seed accounts.', 'error');
    }
  };

  const openNewForm = () => {
    setEditingAccount(null);
    setFormData({ code: '', name: '', category: 'ASSET', sub_category: 'CURRENT_ASSET', description: '', is_active: true });
    setShowForm(true);
  };

  const openEditForm = (acct) => {
    setEditingAccount(acct);
    setFormData({ code: acct.code, name: acct.name, category: acct.category, sub_category: acct.sub_category, description: acct.description || '', is_active: acct.is_active });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAccount) {
        await updateGLAccount(authAxios, editingAccount.id, formData);
        showFormMessage('Account updated.', 'success');
      } else {
        await createGLAccount(authAxios, formData);
        showFormMessage('Account created.', 'success');
      }
      setShowForm(false);
      fetchAccounts();
    } catch (err) {
      showFormMessage(err.response?.data?.detail || 'Failed to save account.', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this account?')) return;
    try {
      await deleteGLAccount(authAxios, id);
      showFormMessage('Account deleted.', 'success');
      fetchAccounts();
    } catch (err) {
      showFormMessage('Cannot delete account with journal entries.', 'error');
    }
  };

  const filteredAccounts = useMemo(() => {
    return accounts.filter(a => {
      if (categoryFilter && a.category !== categoryFilter) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return a.code.toLowerCase().includes(term) || a.name.toLowerCase().includes(term);
      }
      return true;
    });
  }, [accounts, categoryFilter, searchTerm]);

  const groupedAccounts = useMemo(() => {
    const groups = {};
    for (const acct of filteredAccounts) {
      const cat = acct.category || 'OTHER';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(acct);
    }
    return groups;
  }, [filteredAccounts]);

  const subCategoryOptions = SUB_CATEGORY_MAP[formData.category] || [];

  return (
    <div className="animate__animated animate__fadeIn">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Chart of Accounts</h2>
          <p className="text-gray-500 text-sm mt-1">Manage your firm's general ledger accounts</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <button onClick={handleSeed} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium">
              Seed Defaults
            </button>
            <button onClick={openNewForm} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium">
              + New Account
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input type="text" placeholder="Search by code or name..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">All Categories</option>
          {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold mb-4">{editingAccount ? 'Edit Account' : 'New Account'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                  <input type="text" required value={formData.code} onChange={e => setFormData({...formData, code: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. 1000" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input type="text" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Bank Account" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select value={formData.category} onChange={e => { const cat = e.target.value; const subs = SUB_CATEGORY_MAP[cat] || []; setFormData({...formData, category: cat, sub_category: subs[0]?.value || ''}); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sub-Category</label>
                  <select value={formData.sub_category} onChange={e => setFormData({...formData, sub_category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    {subCategoryOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={2} />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.checked})} />
                Active
              </label>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">
                  {editingAccount ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Account listing grouped by category */}
      {isLoading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
      ) : accounts.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <p className="text-gray-500 mb-4">No accounts found. Seed default accounts to get started.</p>
          {canManage && <button onClick={handleSeed} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">Seed Default Accounts</button>}
        </div>
      ) : (
        Object.entries(groupedAccounts).map(([category, accts]) => (
          <div key={category} className="mb-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
              {CATEGORY_OPTIONS.find(c => c.value === category)?.label || category}
            </h3>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sub-Category</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    {canManage && <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {accts.map(a => (
                    <tr key={a.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-gray-700">{a.code}</td>
                      <td className="px-4 py-3 text-gray-800">{a.name}</td>
                      <td className="px-4 py-3 text-gray-500">{(a.sub_category || '').replace(/_/g, ' ')}</td>
                      <td className="px-4 py-3 text-right font-medium text-gray-800">
                        {a.balance != null ? `P ${parseFloat(a.balance).toLocaleString('en-BW', { minimumFractionDigits: 2 })}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${a.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {a.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      {canManage && (
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => openEditForm(a)} className="text-indigo-600 hover:text-indigo-800 text-xs font-medium mr-3">Edit</button>
                          <button onClick={() => handleDelete(a.id)} className="text-red-500 hover:text-red-700 text-xs font-medium">Delete</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export default ChartOfAccounts;
